import type { ReactNode } from 'react';
import { cx } from '../cx';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { LABEL_TEXT, TICK_TEXT, chartStroke } from './scale';

export type BumpChartProps = {
  /** The periods, oldest first ("May", "Jun", ...). */
  periods: ReactNode[];
  /** One item per line; `ranks` is the rank (1 = top) in each period. */
  items: Array<{ id: string; label: ReactNode; ranks: number[] }>;
  /** The story item: ink line with sun rings; the rest context. */
  highlightId?: string;
  /** `auto` (default) switches to the narrow form in a narrow container: a smaller right label gutter with shorter names, and period labels only at the first and last. */
  layout?: ChartLayout;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  className?: string;
};

const GUTTER: Record<ChartLayout, string> = { auto: 'mr-24 @md:mr-28', narrow: 'mr-24', wide: 'mr-28' };
const NAME: Record<ChartLayout, string> = { auto: 'w-24 @md:w-28', narrow: 'w-24', wide: 'w-28' };

/** Ranks over time, one line per item: who rose and who fell. Rank 1 is the top row. */
export function BumpChart({ periods, items, highlightId, layout = 'auto', description, className }: BumpChartProps) {
  const ranks = Math.max(...items.flatMap((i) => i.ranks));
  const x = (i: number) => (periods.length === 1 ? 50 : (i / (periods.length - 1)) * 100);
  const y = (r: number) => (ranks === 1 ? 50 : ((r - 1) / (ranks - 1)) * 100);
  const order = [...items.filter((i) => i.id !== highlightId), ...items.filter((i) => i.id === highlightId)];
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      <div className={cx('relative ml-8 h-4', GUTTER[layout])} aria-hidden="true">
        {periods.map((p, i) => (
          <span
            key={i}
            className={cx(
              'absolute -translate-x-1/2',
              TICK_TEXT,
              i !== 0 && i !== periods.length - 1 && WIDE_ONLY[layout],
            )}
            style={{ left: `${x(i)}%` }}
          >
            {p}
          </span>
        ))}
      </div>
      <div className="flex" aria-hidden="true">
        <div className="relative my-2 w-8 shrink-0" style={{ height: `${ranks * 2.25}rem` }}>
          {Array.from({ length: ranks }, (_, k) => (
            <span
              key={k}
              className={cx('absolute right-3 -translate-y-1/2', TICK_TEXT)}
              style={{ top: `${y(k + 1)}%` }}
            >
              {k + 1}
            </span>
          ))}
        </div>
        <div className={cx('relative my-2 flex-1', GUTTER[layout])}>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 size-full overflow-visible">
            {order.map((it) => (
              <polyline
                key={it.id}
                points={it.ranks.map((r, i) => `${x(i)},${y(r)}`).join(' ')}
                fill="none"
                vectorEffect="non-scaling-stroke"
                strokeWidth={it.id === highlightId ? 4 : 3}
                strokeLinejoin="round"
                className={chartStroke(it.id === highlightId ? 'subject' : 'context')}
              />
            ))}
          </svg>
          {order.map((it) =>
            it.ranks.map((r, i) => (
              <span
                key={`${it.id}-${i}`}
                className={cx(
                  'absolute size-3 -translate-1/2 rounded-full ring-2',
                  it.id === highlightId ? 'bg-jc-chart-2 ring-jc-ink' : 'bg-jc-surface ring-jc-chart-context',
                )}
                style={{ left: `${x(i)}%`, top: `${y(r)}%` }}
              />
            )),
          )}
          {items.map((it) => (
            <span
              key={it.id}
              className={cx(
                'absolute left-full -translate-y-1/2 truncate pl-3',
                NAME[layout],
                LABEL_TEXT,
                it.id === highlightId && 'font-semibold text-jc-ink',
              )}
              style={{ top: `${y(it.ranks[it.ranks.length - 1])}%` }}
            >
              {it.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
